import {
  serializeProjectToBundle,
  type SerializeProjectToBundleBlockInput,
} from '@/features/templates/authoring/serialize-project-to-bundle'
import { blockPartsFromBundleFiles, type BlockParts } from './read-block-parts'

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined
}

function blocksOf(project: unknown): Record<string, unknown> {
  return asRecord(asRecord(project)?.blocks) ?? {}
}

/** The blocks this project holds, by name. */
export function projectBlockNames(project: unknown): string[] {
  return Object.keys(blocksOf(project))
}

/**
 * One block of the open project, in the schema vocabulary.
 *
 * Goes through `serializeProjectToBundle` — the pure inverse of the bundle
 * loader, and already the producer `TemplateAuthoringPanel` trusts — rather
 * than reading `currentProject.blocks` directly. The store keeps `block.flow`
 * as the flows map while the schema declares `{ flows: … }`, and reading the
 * store by hand would have to re-implement that wrapping and would drift the
 * day the serializer changes it. One producer owns the shape.
 */
export function projectBlockParts(
  project: unknown,
  blockName: string,
): BlockParts | null {
  const record = asRecord(project)
  if (!record) return null

  const { files } = serializeProjectToBundle({
    manifest: (asRecord(record.manifest) ?? {}) as Record<string, unknown>,
    tokens: (asRecord(record.designSystem) ?? null) as Record<string, unknown> | null,
    derivedTokens: (asRecord(record.tokens) ?? null) as Record<string, unknown> | null,
    blocks: blocksOf(project) as Record<string, SerializeProjectToBundleBlockInput>,
    appDataFiles: (asRecord(record.data) ?? {}) as Record<string, unknown>,
  })

  return blockPartsFromBundleFiles(files as Record<string, unknown>, blockName)
}

/**
 * What to call a block on screen.
 *
 * A block's own name is its address (`sipass-admin-courses-new__root`); the
 * page that holds it carries the name a person gave it (`New Golf Course`).
 * That name is already in the manifest and nothing read it, so the block list
 * showed slugs.
 *
 * Same choice the press-chain makes between an element id and its
 * `semanticRole`: the id is where a thing lives, the name is what it is
 * called. A block no page claims keeps its own name — inventing one would be
 * worse than showing the address.
 */
export function projectBlockLabel(project: unknown, blockName: string): string {
  const pages = asRecord(asRecord(project)?.manifest)?.pages
  if (Array.isArray(pages)) {
    for (const entry of pages) {
      const page = asRecord(entry)
      const held = page?.blocks
      if (!Array.isArray(held) || !held.includes(blockName)) continue
      const name = page?.name
      if (typeof name === 'string' && name.trim() !== '') return name
    }
  }
  return blockName
}
